import { useState, useRef } from 'react';
import SRCards from '../../components/SRCards';
import {
    Button, Container, TextField, CircularProgress, Chip, Snackbar,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Paper, IconButton, Select, MenuItem, FormControl, Radio, Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import SaveIcon from '@material-ui/icons/Save';
import SelectCompany from '../../components/SelectCompany';
import CompanyApi from '../../services/company.service';

interface StandardStatus {
    id: number;
    name: string;
    displayName: string;
    color: string;
    sortPosition: string | number;
}

interface CFStatusRow {
    assignmentStatusId: number | '';
    name: string;
    displayName: string;
    color: string;
    isNew: boolean;
    existingId?: number;
    isMigrationDefault: boolean;
}

interface ExistingCFStatus {
    id: number;
    name: string;
    displayName: string;
    color: string;
    sortPosition: string;
    assignmentStatusId: number;
}

export default function CustomAssignmentStatus() {
    const [selectedCompany, setSelectedCompany] = useState<{ title: string; value: number } | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [standardStatuses, setStandardStatuses] = useState<StandardStatus[]>([]);
    const [existingCFStatuses, setExistingCFStatuses] = useState<ExistingCFStatus[]>([]);
    const [editedExisting, setEditedExisting] = useState<ExistingCFStatus[]>([]);
    const [existingDirty, setExistingDirty] = useState(false);
    const [rows, setRows] = useState<CFStatusRow[]>([]);
    const [snackBar, setSnackBar] = useState('');
    const [snackMsg, setSnackMsg] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSavingExisting, setIsSavingExisting] = useState(false);
    const [isDisabling, setIsDisabling] = useState(false);
    const [showDisableConfirm, setShowDisableConfirm] = useState(false);

    // drag refs for new rows
    const newDragIndex = useRef<number | null>(null);
    const newDragOverIndex = useRef<number | null>(null);
    // drag refs for existing rows
    const exDragIndex = useRef<number | null>(null);
    const exDragOverIndex = useRef<number | null>(null);

    const api = new CompanyApi();

    const handleCompanySelect = async (company: { title: string; value: number } | null) => {
        setSelectedCompany(company);
        setIsEnabled(false);
        setRows([]);
        setStandardStatuses([]);
        setExistingCFStatuses([]);
        setEditedExisting([]);
        setExistingDirty(false);
        if (!company) return;

        setIsChecking(true);
        try {
            const [stdStatuses, ticketSettings, cfStatuses] = await Promise.all([
                api.getStandardAssignmentStatuses(company.value),
                api.getTargetCompanyTicketSettings(company.value),
                api.getCustomAssignmentStatuses(company.value),
            ]);

            const std: StandardStatus[] = Array.isArray(stdStatuses) ? stdStatuses : [];
            setStandardStatuses(std);

            const enabled = !!(ticketSettings && ticketSettings.isAssignmentCustomStatusEnabled);
            setIsEnabled(enabled);

            if (enabled) {
                const cf: ExistingCFStatus[] = Array.isArray(cfStatuses) ? cfStatuses : [];
                setExistingCFStatuses(cf);
                setEditedExisting(cf.map(s => ({ ...s })));
                setRows([]);
            } else {
                const prefilledRows: CFStatusRow[] = std.map((s) => ({
                    assignmentStatusId: s.id,
                    name: s.name,
                    displayName: s.displayName || s.name,
                    color: s.color || '#000000',
                    isNew: true,
                    isMigrationDefault: true, // 1:1 mapping initially, every row is its own default
                }));
                setRows(prefilledRows);
            }
        } catch (err) {
            setSnackBar('error');
            setSnackMsg('Failed to load company data.');
        } finally {
            setIsChecking(false);
        }
    };

    // ── New rows helpers ─────────────────────────────────────────────────────

    const updateRow = (idx: number, field: keyof CFStatusRow, value: any) => {
        setRows(prev => {
            const updated = prev.map((r, i) => i === idx ? { ...r, [field]: value } : r);
            if (field === 'assignmentStatusId') {
                const oldStdId = prev[idx].assignmentStatusId;
                // Clear isMigrationDefault on the moving row — it's leaving its old group
                let result = updated.map((r, i) => i === idx ? { ...r, isMigrationDefault: false } : r);
                // If new group has no default, make this row the default
                const newGroupHasDefault = result.some((r, i) => i !== idx && r.assignmentStatusId === value && r.isMigrationDefault);
                if (!newGroupHasDefault) {
                    result = result.map((r, i) => i === idx ? { ...r, isMigrationDefault: true } : r);
                }
                // If old group now has no default, assign first remaining row in that group
                const oldGroupHasDefault = result.some(r => r.assignmentStatusId === oldStdId && r.isMigrationDefault);
                if (!oldGroupHasDefault) {
                    const firstInOldGroup = result.findIndex(r => r.assignmentStatusId === oldStdId);
                    if (firstInOldGroup !== -1) {
                        result = result.map((r, i) => i === firstInOldGroup ? { ...r, isMigrationDefault: true } : r);
                    }
                }
                return result;
            }
            return updated;
        });
    };

    const setMigrationDefault = (idx: number) => {
        setRows(prev => {
            const targetStdId = prev[idx].assignmentStatusId;
            return prev.map((r, i) => ({
                ...r,
                isMigrationDefault: r.assignmentStatusId === targetStdId ? i === idx : r.isMigrationDefault,
            }));
        });
    };

    const addRow = () => {
        setRows(prev => {
            const defaultStdId = standardStatuses.length > 0 ? standardStatuses[0].id : '';
            const hasDefault = prev.some(r => r.assignmentStatusId === defaultStdId && r.isMigrationDefault);
            return [...prev, {
                assignmentStatusId: defaultStdId,
                name: '',
                displayName: '',
                color: '#000000',
                isNew: true,
                isMigrationDefault: !hasDefault,
            }];
        });
    };

    const removeRow = (idx: number) => {
        setRows(prev => prev.filter((_, i) => i !== idx));
    };

    const handleNewDragStart = (idx: number) => { newDragIndex.current = idx; };
    const handleNewDragEnter = (idx: number) => { newDragOverIndex.current = idx; };
    const handleNewDragEnd = () => {
        const from = newDragIndex.current;
        const to = newDragOverIndex.current;
        if (from !== null && to !== null && from !== to) {
            setRows(prev => {
                const updated = [...prev];
                const [moved] = updated.splice(from, 1);
                updated.splice(to, 0, moved);
                return updated;
            });
        }
        newDragIndex.current = null;
        newDragOverIndex.current = null;
    };

    // ── Existing rows helpers ────────────────────────────────────────────────

    const updateExisting = (idx: number, field: keyof ExistingCFStatus, value: any) => {
        setEditedExisting(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
        setExistingDirty(true);
    };

    const handleExDragStart = (idx: number) => { exDragIndex.current = idx; };
    const handleExDragEnter = (idx: number) => { exDragOverIndex.current = idx; };
    const handleExDragEnd = () => {
        const from = exDragIndex.current;
        const to = exDragOverIndex.current;
        if (from !== null && to !== null && from !== to) {
            setEditedExisting(prev => {
                const updated = [...prev];
                const [moved] = updated.splice(from, 1);
                updated.splice(to, 0, moved);
                return updated;
            });
            setExistingDirty(true);
        }
        exDragIndex.current = null;
        exDragOverIndex.current = null;
    };

    const handleSaveExisting = async () => {
        if (!selectedCompany) return;
        const names = editedExisting.map(s => s.name.trim().toLowerCase());
        if (new Set(names).size !== names.length) {
            setSnackBar('error');
            setSnackMsg('Custom status names must be unique.');
            return;
        }
        setIsSavingExisting(true);
        try {
            const customStatuses = editedExisting.map(s => ({
                id: s.id,
                name: s.name,
                displayName: s.displayName,
                color: s.color,
                assignmentStatusId: s.assignmentStatusId,
            }));
            await api.updateCustomAssignmentStatuses({
                companyId: selectedCompany.value,
                customStatuses,
            });
            // Refresh to get updated sortPositions from backend
            const updated = await api.getCustomAssignmentStatuses(selectedCompany.value);
            const cf: ExistingCFStatus[] = Array.isArray(updated) ? updated : [];
            setExistingCFStatuses(cf);
            setEditedExisting(cf.map(s => ({ ...s })));
            setExistingDirty(false);
            setSnackBar('success');
            setSnackMsg('Custom statuses updated successfully.');
        } catch (err: any) {
            setSnackBar('error');
            setSnackMsg(`Failed to save: ${err?.message || err}`);
        } finally {
            setIsSavingExisting(false);
        }
    };

    // ── Submit helpers ───────────────────────────────────────────────────────

    const buildCustomStatuses = (rowList: CFStatusRow[]) =>
        rowList.map(r => ({
            name: r.name.trim(),
            displayName: r.displayName.trim() || r.name.trim(),
            color: r.color,
            assignmentStatusId: Number(r.assignmentStatusId),
            description: '',
            isMigrationDefault: r.isMigrationDefault,
        }));

    const validateRows = (rowList: CFStatusRow[], existingNames: string[] = []) => {
        for (const r of rowList) {
            if (!r.name.trim()) {
                setSnackBar('error');
                setSnackMsg('All rows must have a name.');
                return false;
            }
            if (!r.assignmentStatusId) {
                setSnackBar('error');
                setSnackMsg('All rows must be mapped to a standard status.');
                return false;
            }
        }
        const newNames = rowList.map(r => r.name.trim().toLowerCase());
        if (new Set(newNames).size !== newNames.length) {
            setSnackBar('error');
            setSnackMsg('Custom status names must be unique.');
            return false;
        }
        const existingLower = new Set(existingNames.map(n => n.toLowerCase()));
        for (const r of rowList) {
            if (existingLower.has(r.name.trim().toLowerCase())) {
                setSnackBar('error');
                setSnackMsg(`"${r.name}" already exists as a custom status.`);
                return false;
            }
        }
        return true;
    };

    const handleEnable = async () => {
        if (!selectedCompany) return;
        if (rows.length === 0) {
            setSnackBar('error');
            setSnackMsg('Add at least one custom status before enabling.');
            return;
        }
        if (!validateRows(rows)) return;

        setIsSubmitting(true);
        try {
            const customStatuses = buildCustomStatuses(rows);
            await api.enableCustomAssignmentStatus({
                companyId: selectedCompany.value,
                customStatuses,
            });
            // Refresh from API so sortPositions are visible
            const updated = await api.getCustomAssignmentStatuses(selectedCompany.value);
            const cf: ExistingCFStatus[] = Array.isArray(updated) ? updated : [];
            setSnackBar('success');
            setSnackMsg('Custom assignment status enabled successfully.');
            setIsEnabled(true);
            setExistingCFStatuses(cf);
            setEditedExisting(cf.map(s => ({ ...s })));
            setRows([]);
        } catch (err: any) {
            setSnackBar('error');
            setSnackMsg(`Failed to enable: ${err?.message || err}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddMore = async () => {
        if (!selectedCompany) return;
        const newRows = rows.filter(r => r.isNew);
        if (newRows.length === 0) {
            setSnackBar('error');
            setSnackMsg('No new statuses to add.');
            return;
        }
        if (!validateRows(newRows, existingCFStatuses.map(s => s.name))) return;

        setIsSubmitting(true);
        try {
            const customStatuses = buildCustomStatuses(newRows);
            await api.addCustomAssignmentStatuses({
                companyId: selectedCompany.value,
                customStatuses,
            });
            setSnackBar('success');
            setSnackMsg(`${customStatuses.length} custom status(es) added successfully.`);
            setRows([]);
            const updated = await api.getCustomAssignmentStatuses(selectedCompany.value);
            const cf: ExistingCFStatus[] = Array.isArray(updated) ? updated : [];
            setExistingCFStatuses(cf);
            setEditedExisting(cf.map(s => ({ ...s })));
        } catch (err: any) {
            setSnackBar('error');
            setSnackMsg(`Failed to add: ${err?.message || err}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDisable = async () => {
        if (!selectedCompany) return;
        setIsDisabling(true);
        try {
            await api.disableCustomAssignmentStatus({ companyId: selectedCompany.value });
            setIsEnabled(false);
            setShowDisableConfirm(false);
            setExistingCFStatuses([]);
            setEditedExisting([]);
            setSnackBar('success');
            setSnackMsg('Custom assignment status disabled.');
        } catch (err: any) {
            setSnackBar('error');
            setSnackMsg(`Failed to disable: ${err?.message || err}`);
        } finally {
            setIsDisabling(false);
        }
    };

    return (
        <>
            <SRCards />
            <Container>
                <SelectCompany optionHandler={handleCompanySelect} />
                <br /><br />

                {isChecking && <CircularProgress size={24} />}

                {!isChecking && selectedCompany && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                            <Chip
                                label={isEnabled ? 'Custom Status Enabled' : 'Not Enabled'}
                                color={isEnabled ? 'primary' : 'default'}
                            />
                            {isEnabled && !showDisableConfirm && (
                                <>
                                    <span style={{ fontSize: 13, color: '#555' }}>
                                        Drag rows to reorder. Edit inline. Click <strong>Save Changes</strong> to persist.
                                    </span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        style={{ marginLeft: 'auto', color: '#d32f2f', borderColor: '#d32f2f' }}
                                        onClick={() => setShowDisableConfirm(true)}
                                    >
                                        Disable Feature
                                    </Button>
                                </>
                            )}
                            {isEnabled && showDisableConfirm && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                                    <span style={{ fontSize: 13, color: '#d32f2f', fontWeight: 500 }}>
                                        Disable custom assignment status for this company?
                                    </span>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        style={{ background: '#d32f2f', color: '#fff' }}
                                        disabled={isDisabling}
                                        onClick={handleDisable}
                                    >
                                        {isDisabling ? <CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} /> : null}
                                        Yes, Disable
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={isDisabling}
                                        onClick={() => setShowDisableConfirm(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Existing CF Statuses — editable + draggable */}
                        {isEnabled && editedExisting.length > 0 && (
                            <div style={{ marginBottom: 24 }}>
                                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#333' }}>
                                    Current Custom Statuses
                                </div>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow style={{ background: '#f5f5f5' }}>
                                                <TableCell style={{ width: 32 }} />
                                                <TableCell><strong>Sort</strong></TableCell>
                                                <TableCell><strong>Name</strong></TableCell>
                                                <TableCell><strong>Display Name</strong></TableCell>
                                                <TableCell><strong>Color</strong></TableCell>
                                                <TableCell><strong>Maps To Standard</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {editedExisting.map((cf, i) => (
                                                    <TableRow
                                                        key={cf.id}
                                                        draggable
                                                        onDragStart={() => handleExDragStart(i)}
                                                        onDragEnter={() => handleExDragEnter(i)}
                                                        onDragEnd={handleExDragEnd}
                                                        onDragOver={e => e.preventDefault()}
                                                        style={{ cursor: 'grab' }}
                                                    >
                                                        <TableCell style={{ color: '#aaa', paddingRight: 0 }}>
                                                            <DragIndicatorIcon fontSize="small" />
                                                        </TableCell>
                                                        <TableCell style={{ color: '#888', fontSize: 13 }}>
                                                            {String(i + 1).padStart(2, '0')}
                                                        </TableCell>
                                                        <TableCell style={{ minWidth: 220 }}>
                                                            <TextField
                                                                size="small"
                                                                variant="outlined"
                                                                value={cf.name}
                                                                onChange={e => updateExisting(i, 'name', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell style={{ minWidth: 220 }}>
                                                            <TextField
                                                                size="small"
                                                                variant="outlined"
                                                                value={cf.displayName}
                                                                onChange={e => updateExisting(i, 'displayName', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell style={{ minWidth: 110 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <input
                                                                    type="color"
                                                                    value={cf.color}
                                                                    onChange={e => updateExisting(i, 'color', e.target.value)}
                                                                    style={{ width: 36, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                                                />
                                                                <TextField
                                                                    size="small"
                                                                    variant="outlined"
                                                                    value={cf.color}
                                                                    onChange={e => updateExisting(i, 'color', e.target.value)}
                                                                    inputProps={{ style: { width: 80, fontSize: 12 } }}
                                                                />
                                                            </div>
                                                        </TableCell>
                                                        <TableCell style={{ minWidth: 180 }}>
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    value={cf.assignmentStatusId}
                                                                    onChange={e => updateExisting(i, 'assignmentStatusId', e.target.value)}
                                                                    variant="outlined"
                                                                >
                                                                    {standardStatuses.map(s => (
                                                                        <MenuItem key={s.id} value={s.id}>
                                                                            <span style={{
                                                                                display: 'inline-block', width: 12, height: 12,
                                                                                background: s.color, borderRadius: 2,
                                                                                marginRight: 6, verticalAlign: 'middle'
                                                                            }} />
                                                                            {s.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                    </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {existingDirty && (
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={isSavingExisting
                                                ? <CircularProgress size={14} color="inherit" />
                                                : <SaveIcon />}
                                            disabled={isSavingExisting}
                                            onClick={handleSaveExisting}
                                        >
                                            Save Changes
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Add-More Form */}
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#333' }}>
                            {isEnabled ? 'Add New Custom Statuses' : 'Define Custom Statuses'}
                        </div>
                        {rows.length > 0 && (
                            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                                Drag rows to reorder — sort position is assigned automatically.
                            </div>
                        )}

                        {rows.length > 0 && (
                            <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 12 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow style={{ background: '#f5f5f5' }}>
                                            <TableCell style={{ width: 32 }} />
                                            <TableCell><strong>Maps To Standard Status</strong></TableCell>
                                            <TableCell><strong>Custom Name</strong></TableCell>
                                            <TableCell><strong>Display Name</strong></TableCell>
                                            <TableCell><strong>Color</strong></TableCell>
                                            <TableCell>
                                                <Tooltip title="Which custom status existing assignments should be migrated to when multiple map to the same standard status">
                                                    <strong style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Migration Default</strong>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row, idx) => (
                                            <TableRow
                                                key={idx}
                                                draggable
                                                onDragStart={() => handleNewDragStart(idx)}
                                                onDragEnter={() => handleNewDragEnter(idx)}
                                                onDragEnd={handleNewDragEnd}
                                                onDragOver={e => e.preventDefault()}
                                                style={{ cursor: 'grab' }}
                                            >
                                                <TableCell style={{ color: '#aaa', paddingRight: 0 }}>
                                                    <DragIndicatorIcon fontSize="small" />
                                                </TableCell>
                                                <TableCell style={{ minWidth: 180 }}>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            value={row.assignmentStatusId}
                                                            onChange={e => updateRow(idx, 'assignmentStatusId', e.target.value)}
                                                            variant="outlined"
                                                        >
                                                            {standardStatuses.map(s => (
                                                                <MenuItem key={s.id} value={s.id}>
                                                                    <span style={{
                                                                        display: 'inline-block', width: 12, height: 12,
                                                                        background: s.color, borderRadius: 2,
                                                                        marginRight: 6, verticalAlign: 'middle'
                                                                    }} />
                                                                    {s.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell style={{ minWidth: 220 }}>
                                                    <TextField
                                                        size="small"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={row.name}
                                                        onChange={e => updateRow(idx, 'name', e.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ minWidth: 220 }}>
                                                    <TextField
                                                        size="small"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={row.displayName}
                                                        onChange={e => updateRow(idx, 'displayName', e.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ minWidth: 110 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <input
                                                            type="color"
                                                            value={row.color}
                                                            onChange={e => updateRow(idx, 'color', e.target.value)}
                                                            style={{ width: 36, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            value={row.color}
                                                            onChange={e => updateRow(idx, 'color', e.target.value)}
                                                            inputProps={{ style: { width: 80, fontSize: 12 } }}
                                                        />
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Radio
                                                        size="small"
                                                        checked={row.isMigrationDefault}
                                                        onChange={() => setMigrationDefault(idx)}
                                                        disabled={!rows.some((r, i) => i !== idx && r.assignmentStatusId === row.assignmentStatusId)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton size="small" onClick={() => removeRow(idx)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addRow}
                                size="small"
                            >
                                Add Row
                            </Button>

                            {rows.length > 0 && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    onClick={isEnabled ? handleAddMore : handleEnable}
                                >
                                    {isSubmitting
                                        ? <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                                        : null}
                                    {isEnabled ? 'Add Statuses' : 'Enable Custom Assignment Status'}
                                </Button>
                            )}
                        </div>
                    </>
                )}

                <Snackbar
                    open={snackBar !== ''}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    autoHideDuration={6000}
                    onClose={() => setSnackBar('')}
                >
                    <Alert severity={snackBar === 'error' ? 'error' : 'success'} onClose={() => setSnackBar('')}>
                        {snackMsg}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
}
